import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { defaultHomeFor } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useTheme } from "@/context/ThemeContext";
import { Flame, Moon, Sun, Eye, EyeOff, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate(defaultHomeFor(user.role), { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Bienvenido, ${res.user.nombre}`);
      navigate(defaultHomeFor(res.user.role), { replace: true });
    } else {
      toast.error(res.error || "Error al iniciar sesión");
    }
  };

  const quickLogin = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-zinc-950 text-white">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/c1a89634-175b-4190-9b32-a20a4b45edb3/images/51e840f4a9a991d40ccf012945a021b99211ab7f804ce6327034fcbd56653cb9.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/90 via-zinc-950/70 to-zinc-950/95" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-3">
            <img src="/mkitchen-logo.png" alt="M-Kitchen" className="h-14 w-14 rounded-2xl bg-black p-1 object-contain ember-glow" />
            <div>
              <div className="text-2xl font-black tracking-tight">M-Kitchen</div>
              <div className="text-xs uppercase tracking-[0.3em] text-orange-400">Admin POS</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight">
            Algunas <span className="ember-text">promesas</span> no necesitan<br />
            decirse para seguir vivas.
          </h2>
          <p className="mt-6 text-base text-zinc-300 leading-relaxed">
            M-Kitchen nació para convertir la pasión en una operación excepcional.
          </p>
          <div className="mt-8 flex items-center gap-2 text-xs uppercase tracking-widest text-orange-400">
            <Sparkles className="h-4 w-4" /> v2.5 · México
          </div>
        </div>

        <div className="relative z-10 text-xs text-zinc-500">© 2026 M-Kitchen</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="inline-flex items-center gap-3">
              <img src="/mkitchen-logo.png" alt="M-Kitchen" className="h-12 w-12 rounded-xl bg-black p-1 object-contain" />
              <div>
                <div className="text-xl font-black">M-Kitchen</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin POS</div>
              </div>
            </div>
            <Button variant="outline" size="icon" onClick={toggle} data-testid="theme-toggle-login">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>

          <div className="hidden lg:flex justify-end mb-6">
            <Button variant="outline" size="icon" onClick={toggle} data-testid="theme-toggle-login">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Iniciar sesión</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Ingresa con tu cuenta operativa de M-Kitchen.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                required
                className="h-12"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPass ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base font-bold"
              data-testid="login-submit-btn"
            >
              {loading ? "Ingresando…" : "Iniciar sesión"}
            </Button>
          </form>

        </div>
      </div>
    </div>
  );
}
